/**
 * Utility functions for chat timestamp handling
 */

/**
 * Format a timestamp for the centered timestamp divider
 * Smart formatting based on message age:
 * - Today: "HH:mm" (e.g., "23:52")
 * - This year: "HH:mm DD Tháng M" (e.g., "20:40 16 Tháng 9")
 * - Older: "HH:mm DD Tháng M, YYYY" (e.g., "20:40 16 Tháng 9, 2025")
 */
export function formatTimestampDivider(timestamp: string | Date, locale = 'vi'): string {
  const date = new Date(timestamp)
  const now = new Date()

  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const day = date.getDate()
  const month = date.getMonth() + 1
  const year = date.getFullYear()

  const isToday =
    date.getDate() === now.getDate() && date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear()

  const isThisYear = date.getFullYear() === now.getFullYear()

  if (isToday) {
    return `${hours}:${minutes}`
  }

  if (locale === 'en') {
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ]

    if (isThisYear) {
      return `${hours}:${minutes} ${day} ${monthNames[month - 1]}`
    }

    return `${hours}:${minutes} ${day} ${monthNames[month - 1]}, ${year}`
  }

  if (isThisYear) {
    return `${hours}:${minutes} ${day} Tháng ${month}`
  }
  return `${hours}:${minutes} ${day} Tháng ${month}, ${year}`
}

/**
 * Determine if a timestamp divider should be shown between two messages
 * Show divider if:
 * - It's the first message
 * - More than the specified time has passed between messages (default: 30 minutes)
 * - Messages are on different days
 */
export function shouldShowTimestampDivider(
  currentMessage: { createdAt: string } | null,
  previousMessage: { createdAt: string } | null,
  thresholdMinutes = 30
): boolean {
  if (!previousMessage) {
    return true
  }

  if (!currentMessage) {
    return false
  }

  const currentDate = new Date(currentMessage.createdAt)
  const previousDate = new Date(previousMessage.createdAt)

  const currentDay = currentDate.toDateString()
  const previousDay = previousDate.toDateString()

  if (currentDay !== previousDay) {
    return true
  }

  const timeDiffMs = currentDate.getTime() - previousDate.getTime()
  const timeDiffMinutes = timeDiffMs / (1000 * 60)

  return timeDiffMinutes >= thresholdMinutes
}

/**
 * Get a relative date label for the timestamp divider
 * Returns "Today", "Yesterday", or the formatted date
 */
export function getRelativeDateLabel(timestamp: string | Date, locale = 'vi'): string {
  const date = new Date(timestamp)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  if (date.toDateString() === today.toDateString()) {
    return locale === 'en' ? 'Today' : 'Hôm nay'
  }

  if (date.toDateString() === yesterday.toDateString()) {
    return locale === 'en' ? 'Yesterday' : 'Hôm qua'
  }

  return formatTimestampDivider(timestamp, locale)
}
